import { isProvisional } from "@rally/rating";
import { demoRatings } from "./demo.js";
import { SPORT } from "./sport.js";
import { supabase } from "./supabase.js";
import type { Kind, Sport } from "./types.js";

export interface DisciplineRating {
  sport: Sport;
  kind: Kind;
  r: number;
  rd: number;
  matchesPlayed: number;
  lastPlayedAt: string | null;
}

/**
 * A member's ratings in this app's sport — singles and doubles are separate ratings, so
 * there can be two. A discipline you have never played has no row: the rating is created
 * by the first confirmed match, not by signing up, so the profile shows nothing rather
 * than a fictional 1500.
 *
 * Anybody's, not only your own — `ratings_read` has always been community-wide, because a
 * leaderboard everyone can see is made of exactly these numbers.
 */
export async function fetchRatings(memberId: string): Promise<DisciplineRating[]> {
  if (!supabase) return sampleRatings(memberId);

  const { data, error } = await supabase
    .from("ratings")
    .select("sport, kind, r, rd, matches_played, last_played_at")
    .eq("member_id", memberId)
    .eq("sport", SPORT);

  if (error) throw new Error(error.message);

  return (data ?? []).map((row) => ({
    sport: row.sport as Sport,
    kind: row.kind as Kind,
    r: row.r as number,
    rd: row.rd as number,
    matchesPlayed: row.matches_played as number,
    lastPlayedAt: row.last_played_at as string | null,
  }));
}

function sampleRatings(memberId: string): DisciplineRating[] {
  return demoRatings
    .filter((rating) => rating.memberId === memberId)
    .map((rating) => ({
      sport: SPORT,
      kind: rating.kind,
      r: rating.r,
      rd: rating.rd,
      matchesPlayed: rating.matchesPlayed,
      lastPlayedAt: new Date(Date.now() - 3_600_000 * 3).toISOString(),
    }));
}

/** One confirmed match's effect on a rating, oldest first. */
export interface RatingPoint {
  at: string;
  r: number;
  rd: number;
  provisional: boolean;
}

/**
 * A member's rating over time, per discipline.
 *
 * Read straight from `rating_events`, which has recorded every step since the first
 * migration — the history has always been there, nothing was ever computing it. Ordered by
 * `created_at`, which the rating write sets to the match's `played_at`, so the line follows
 * when matches were played rather than when someone got round to logging them.
 */
export async function fetchRatingHistory(memberId: string, kind: Kind): Promise<RatingPoint[]> {
  if (!supabase) return sampleHistory(memberId, kind);

  const { data, error } = await supabase
    .from("rating_events")
    .select("created_at, r_after, rd_after")
    .eq("member_id", memberId)
    .eq("sport", SPORT)
    .eq("kind", kind)
    .order("created_at", { ascending: true });

  if (error) throw new Error(error.message);

  return (data ?? []).map((row) => ({
    at: row.created_at as string,
    r: row.r_after as number,
    rd: row.rd_after as number,
    provisional: isProvisional(row.rd_after as number),
  }));
}

/**
 * A plausible climb out of provisional, so the chart can be looked at without a backend.
 *
 * Walked backwards from the member's current rating rather than forwards from a seed, so
 * the line ends on the number the lockup above it is showing. A chart that disagrees with
 * the rating printed next to it is worse than no chart.
 */
function sampleHistory(memberId: string, kind: Kind): RatingPoint[] {
  const current = demoRatings.find((row) => row.memberId === memberId && row.kind === kind);
  if (!current) return [];

  const count = current.matchesPlayed;
  const points: RatingPoint[] = [];
  let r = current.r;
  let rd = current.rd;

  for (let i = count - 1; i >= 0; i--) {
    points.unshift({
      at: new Date(Date.now() - (count - 1 - i) * 86_400_000 * 2).toISOString(),
      r,
      rd,
      provisional: isProvisional(rd),
    });
    r -= Math.sin(i / 3) * 22 + (i < 12 ? 9 : 1.5);
    rd = Math.min(350, rd / 0.93);
  }

  return points;
}
